/**
 * SectionHeading.tsx — encabezado uniforme de las secciones del Home.
 *
 * Eyebrow en versalitas violeta flanqueado por dos estrellas doradas, con CTA
 * opcional a la derecha (interno vía `<Link>` o externo vía `<a>`).
 *
 * Extraído de `pages/Home.tsx` (2026-09-22): lo consumen los seis bloques de
 * la página y ahora también `EventsBlock`, que vive en su propio módulo.
 */
import { Link } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { StarDot } from './ornaments';

interface SectionHeadingProps {
  id?: string;
  eyebrow: string;
  ctaLabel?: string;
  ctaHref?: string;
  ctaExternal?: boolean;
  align?: 'center' | 'split';
}

export function SectionHeading({
  id,
  eyebrow,
  ctaLabel,
  ctaHref,
  ctaExternal,
  align = 'split',
}: SectionHeadingProps) {
  const headerWrapCls =
    align === 'center'
      ? 'mb-10 flex flex-col items-center gap-2 text-center'
      : 'mb-10 flex flex-col items-start gap-2 sm:flex-row sm:items-end sm:justify-between';

  const titleNode = (
    <h2
      id={id}
      className={cn(
        'font-display text-h3 uppercase tracking-[0.18em] text-primary-700',
        'inline-flex items-center gap-3',
      )}
    >
      <StarDot className="text-sm" />
      {eyebrow}
      <StarDot className="text-sm" />
    </h2>
  );

  return (
    <div className={headerWrapCls}>
      {titleNode}
      {ctaLabel && ctaHref ? (
        ctaExternal ? (
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-button font-semibold uppercase tracking-[0.04em] text-primary-700 transition-colors duration-2 ease-out hover:text-primary-900"
          >
            {ctaLabel} &rarr;
          </a>
        ) : (
          <Link
            to={ctaHref}
            className="text-button font-semibold uppercase tracking-[0.04em] text-primary-700 transition-colors duration-2 ease-out hover:text-primary-900"
          >
            {ctaLabel} &rarr;
          </Link>
        )
      ) : null}
    </div>
  );
}
